import { drProfileClient } from '@/common/apis/client';
import { getDoctorInsuranceFromBiko } from '@/common/apis/services/biko/getDoctorInsurance';
import { useQueries } from '@tanstack/react-query';

export type DoctorInsurance = { base: string[]; supplement: string[] };

/**
 * The insurances a doctor lists on their own profile (the profile's insurance section reads the same Biko row):
 * slug → user_id (DrProfile) → Biko. null when nothing is listed or a call fails — the card then falls back to the
 * search hit's `insurances` or shows none.
 */
export const fetchDoctorInsuranceBySlug = async (slug: string): Promise<DoctorInsurance | null> => {
  try {
    const { data } = await drProfileClient.get(`/api/doctors/${encodeURIComponent(slug)}`, { timeout: 3000 });
    const userId = data?.user_id;
    if (!userId) return null;
    const insurance = await getDoctorInsuranceFromBiko(userId, { timeoutMs: 3000 });
    if (!insurance) return null;
    const names = (items?: { name?: string }[]) =>
      Array.from(new Set((items ?? []).map(item => item?.name?.trim()).filter((name): name is string => !!name)));
    const result = { base: names(insurance.base), supplement: names(insurance.supplement) };
    return result.base.length || result.supplement.length ? result : null;
  } catch {
    return null;
  }
};

/** One cached lookup per doctor (10 min), only while `enabled`; never retried — a failure just shows no insurance. */
export const useDoctorsInsurance = (slugs: string[], enabled: boolean) => {
  const results = useQueries({
    queries: slugs.map(slug => ({
      queryKey: ['local-specialists-insurance', slug],
      queryFn: () => fetchDoctorInsuranceBySlug(slug),
      enabled: enabled && !!slug,
      staleTime: 10 * 60_000,
      retry: false,
      refetchOnWindowFocus: false,
    })),
  });
  return Object.fromEntries(slugs.map((slug, index) => [slug, results[index]?.data ?? null])) as Record<
    string,
    DoctorInsurance | null
  >;
};
